import * as yup from 'yup';
import mapValues from 'lodash/mapValues';
import { validationMode, validationMessages } from 'helpers/form';
import { CalculationFrequency } from 'modules/BenchmarkForm';
import { BenchmarkDetailsErrors } from 'helpers/errorMessages';

export const compositeWeightedTimeWindowShape = yup.object().shape({
  fromDate: yup
    .date()
    .typeError(validationMessages.required)
    .required(validationMessages.required),
  assetSelectionSetId: yup.string().required(validationMessages.required),
  tagRefPropertyDefinitionId: yup
    .number()
    .typeError(validationMessages.number)
    .nullable(true)
    .required(validationMessages.required),
  tagIds: yup.string().typeError(validationMessages.required),
  lineItems: yup
    .array()
    .of(
      yup.object().shape({
        returnComponentResourceId: yup
          .mixed()
          .required(validationMessages.required),
        weightingComponentTagId: yup
          .mixed()
          .required(validationMessages.required),
      })
    )
    .required()
    .min(1, validationMessages.required),
});

/**
 * Validation Scheme used for Composite Weighted Benchmarks
 * @param mode Create | Edit - change the shape of the schema object based on Creating/Editing a benchmark.
 * @returns return yup schema object
 */
export const compositeWeightedValidationSchema = (
  mode: validationMode = validationMode.Create
) => {
  return yup.lazy((obj) =>
    yup.object(
      mapValues(obj, (value, key) => {
        switch (key) {
          case 'timeWindows':
            return mode === validationMode.Create
              ? yup.array().of(compositeWeightedTimeWindowShape)
              : yup.mixed().notRequired();
          case 'name':
            return yup
              .string()
              .trim()
              .required(validationMessages.benchmarkNameNotEmpty);
          case 'calculationFrequencyId':
            return yup
              .number()
              .typeError(validationMessages.required)
              .required(validationMessages.required);
          case 'baseCurrencyCode':
            return yup
              .string()
              .nullable()
              .typeError(validationMessages.required)
              .required(validationMessages.required);
          case 'weightingMethodologyId':
            return yup.number().required(validationMessages.required);
          case 'customLagDate':
            return yup.string().when(['isLagged', 'setCustomLagDate'], {
              is: (isLagged: string, setCustomLagDate: string) =>
                isLagged === 'true' && setCustomLagDate === 'false',
              then: (schema) => schema.required('Please enter a valid date'),
              otherwise: (schema) => schema.notRequired(),
            });
          case 'pushTimeWindowsToPortfolioAnalytics':
            return yup.boolean().when(['calculationFrequencyId'], {
              is: (calculationFrequencyId: number) => {
                return (
                  value === 'true' &&
                  calculationFrequencyId == CalculationFrequency.day
                );
              },
              then: (schema) => {
                return schema.isFalse(
                  BenchmarkDetailsErrors.cantPushTimeWindowsToPA
                );
              },
              otherwise: (schema) => {
                return schema.notRequired();
              },
            });
          case 'lookThroughDepth':
            return yup
            .number()
              .min(1)
              .max(5)
              .typeError(validationMessages.required)
              .required(validationMessages.required);
          case 'description':
            return yup
              .string()
              .notRequired()
              .max(350, validationMessages.notExceed350);
          default:
            return yup.mixed().notRequired();
        }
      })
    )
  );
};
